import { useState } from 'react';
import { Pause, Ban, ShieldCheck, RefreshCw, Play, AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

export interface PauseAutomationDialogProps {
  isOpen: boolean;
  channel: 'email' | 'sms';
  /** Shown in the prompt so the user can see which automation they hit Pause on. */
  automationName?: string;
  /** Contacts currently mid-sequence — they are the ones a pause holds. */
  activeContacts?: number;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

/**
 * Pause confirmation shared by the email and SMS automation stats and editor
 * pages, so all four entry points warn with the same wording.
 *
 * Built on Modal rather than ConfirmDialog: this needs a titled header with an
 * icon and a structured body, where ConfirmDialog renders its title inside the
 * body next to a round icon and puts `message` in a <p> (no block children).
 *
 * The wording is deliberately specific about the one non-obvious consequence:
 * pausing does not shift anyone's scheduled send time, so every step that comes
 * due during the pause is still due the moment the automation resumes and goes
 * out as a catch-up batch. Everything else about a pause is reversible and
 * loses nothing — see the step processors (paused automations are skipped) and
 * the activate endpoints (re-enrollment only happens from 'draft').
 */
export function PauseAutomationDialog({
  isOpen,
  channel,
  automationName,
  activeContacts,
  onClose,
  onConfirm,
}: PauseAutomationDialogProps) {
  const [loading, setLoading] = useState(false);

  const noun = channel === 'email' ? 'emails' : 'messages';
  const heldNote =
    typeof activeContacts === 'number' && activeContacts > 0
      ? ` ${activeContacts.toLocaleString()} contact${activeContacts === 1 ? '' : 's'} mid-sequence will be held in place.`
      : '';

  const effects = [
    {
      Icon: Ban,
      iconClass: 'text-rose-500 bg-rose-50',
      title: `No ${noun} go out`,
      body: `Sending stops as soon as you confirm.${heldNote}`,
    },
    {
      Icon: ShieldCheck,
      iconClass: 'text-emerald-600 bg-emerald-50',
      title: 'Nothing is deleted',
      body: 'Contacts keep their place in the sequence, and all history and stats are preserved.',
    },
    {
      Icon: RefreshCw,
      iconClass: 'text-slate-500 bg-slate-100',
      title: 'Contacts stop syncing',
      body: 'The daily list sync skips paused automations, so the contact list will not update on its own. The Refresh button still works.',
    },
    {
      Icon: Play,
      iconClass: 'text-sky-600 bg-sky-50',
      title: 'Resuming does not restart anyone',
      body: 'Contacts pick up from their current step — nobody receives step 1 again.',
    },
  ];

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      size="md"
      hideCloseButton={loading}
      title={
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
            <Pause className="h-4 w-4 text-amber-700" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-base font-semibold text-slate-900">Pause Automation</span>
            {automationName && (
              <span className="block truncate text-xs text-slate-500">{automationName}</span>
            )}
          </span>
        </div>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Keep Running
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading}>
            Pause Automation
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          This automation will stop sending until you resume it. Here is what happens:
        </p>

        <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
          {effects.map(({ Icon, iconClass, title, body }) => (
            <li key={title} className="flex gap-3 px-3.5 py-3">
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${iconClass}`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{title}</p>
                <p className="mt-0.5 text-sm leading-snug text-slate-500">{body}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Before you resume
          </p>
          <p className="mt-1 text-sm leading-snug text-amber-900">
            Scheduled send times are not pushed back while paused. Every step that comes due during
            the pause is still due when you resume, so a long pause means those {noun} all send as a
            catch-up batch shortly after resuming.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default PauseAutomationDialog;
